<template>
  <q-list class="userDropdown" :class="{ 'userDropdown--compact': !canViewResourceMetrics }">
    <!-- User Profile Card with Storage and Team Metrics - Only visible to Client role -->
    <div v-if="canViewResourceMetrics" class="user-profile-card">
      <div class="user-header">
        <div class="user-avatar">
          {{ userInitial }}
        </div>
        <div class="user-details">
          <div class="user-name">{{ user.name }}</div>
          <div class="user-role">{{ userRole }}</div>
        </div>
        <div class="plan-badge">{{ planName }}</div>
      </div>

      <!-- Storage Meter with transition -->
      <transition name="fade" mode="out-in">
        <div key="storage-meter" class="resource-meter">
          <div class="resource-header">
            <span>Available storage</span>
            <span class="resource-value">{{ cachedMetrics.formattedMaxStorage }}</span>
          </div>
          <div class="meter-bar-container">
            <div class="meter-bar" :style="{ width: `${cachedMetrics.storagePercentage}%`, background: cachedMetrics.storageBarColor }"></div>
          </div>
          <div class="resource-info">Using {{ cachedMetrics.formattedUsedStorage }} of {{ cachedMetrics.formattedMaxStorage }} ({{ cachedMetrics.storagePercentage }}%)</div>
        </div>
      </transition>

      <!-- Team Members Meter with transition -->
      <transition name="fade" mode="out-in">
        <div key="team-members-meter" class="resource-meter team-members-meter">
          <div class="team-members-content">
            <div class="team-members-info">
              <div class="team-members-title">Team members</div>
              <div class="team-members-count">{{ cachedMetrics.totalMembers }} out of {{ cachedMetrics.maxMembers }} slots used</div>
            </div>
            <div class="circular-meter-container">
              <q-circular-progress show-value class="circular-meter" :value="cachedMetrics.memberPercentage" :color="cachedMetrics.memberBarColor" track-color="grey-3" size="40px" :thickness="0.15">
                <div class="meter-text">{{ cachedMetrics.totalMembers }}</div>
              </q-circular-progress>
            </div>
          </div>
        </div>
      </transition>

      <!-- Upgrade Button -->
      <q-btn class="upgrade-btn" flat no-caps label="Upgrade" @click="handleUpgrade" />
    </div>

    <!-- Separator between user card and menu items - Only shown when profile card is visible -->
    <q-separator v-if="canViewResourceMetrics" class="dropdown-separator" />

    <!-- Menu Items -->
    <template v-for="(element, index) in dropDownElements">
      <q-item v-if="!element.isDisabled" :key="index" clickable class="userDropdown__box">
        <q-item-section class="userDropdown__item" @click="handleAction(element.action)">
          <q-icon :name="`o_${element.icon}`" class="userDropdown__icon" />
          {{ element.name }}
        </q-item-section>

        <BaseTooltip v-if="element.isTooltipEnabled === true" :tooltip="tooltip" />
      </q-item>
    </template>
  </q-list>
</template>

<style lang="scss" scoped>
// Common mixins and variables
$border-radius-sm: 4px;
$border-radius-md: 8px;
$border-radius-lg: 12px;
$spacing-xs: 4px;
$spacing-sm: 8px;
$spacing-md: 12px;
$spacing-lg: 16px;
$bg-light: #f0f0f0;
$bg-white: #fff;
$shadow-dropdown: 0 4px 16px rgba(0, 0, 0, 0.08);
$border-light: 1px solid rgba(151, 151, 151, 0.2);
$icon-color: rgba(0, 0, 0, 0.7);

// Reusable positioning mixins
@mixin flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

@mixin flex-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@mixin absolute-fill {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

// Main dropdown container
.userDropdown {
  @include inter-font($size: 14px, $bolded: false, $color: var(--main-text-color));
  background: $bg-white;
  border: $border-light;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-dropdown;
  min-width: 290px;
  max-width: 290px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
  z-index: 9999;
  position: relative;
  transform: none; /* Prevent transform animations */
  margin: 0;
  left: -10px; /* Adjust horizontal position to align with button */

  // Compact styling for non-client users
  &--compact {
    min-width: 220px;
    max-width: 220px;
    left: 0;
  }

  // Custom scrollbar styling
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  &::-webkit-scrollbar {
    width: 6px;

    &-track {
      background: transparent;
    }

    &-thumb {
      background-color: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    }
  }

  // Item box styling
  &__box {
    margin: 0;
    width: auto;
    min-width: auto;
    max-width: none;
    @include inter-font($size: 14px, $bolded: false, $color: var(--main-text-color));

    &:hover,
    &:active {
      background-color: var(--terziary-btn-color);
    }
  }

  // Item content styling
  &__item {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: $spacing-md $spacing-lg;
    width: 100%;
  }

  // Icon styling
  &__icon {
    font-size: 20px;
    margin-right: $spacing-md;
    color: $icon-color;
  }

  // Quasar item styling
  .q-item {
    min-height: auto;
    padding: 0;
    width: 100%;

    &:first-child {
      margin-top: $spacing-xs;
    }

    &:hover {
      font-weight: inherit;
      color: inherit;
    }
  }
}

// User profile card section
.user-profile-card {
  padding: $spacing-lg;
  background: $bg-white;
  flex-shrink: 0;
  width: 100%;
}

// User header section
.user-header {
  @include flex-between;
  margin-bottom: $spacing-lg;
  width: 100%;
}

// User avatar
.user-avatar {
  @include flex-center;
  @include mona-sans-font($size: 14px, $bolded: true, $color: var(--main-text-color));
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--main-btn-color);
  color: var(--main-text-color);
  font-weight: bold;
  flex-shrink: 0;
  margin-right: $spacing-md;
}

// User details styling
.user-details {
  flex: 1;
  min-width: 0;

  .user-name {
    @include mona-sans-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }

  .user-role {
    @include inter-font($size: 12px, $bolded: false, $color: var(--main-text-color));
  }
}

// Plan badge styling
.plan-badge {
  @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color));
  background-color: $bg-light;
  padding: $spacing-xs $spacing-sm;
  border-radius: $border-radius-sm;
  flex-shrink: 0;
}

// Resource metrics section
.resource-meter {
  margin-bottom: $spacing-lg;
  width: 100%;

  &.team-members-meter {
    margin: $spacing-lg 0;
  }
}

// Resource header styling
.resource-header {
  @include flex-between;
  @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color), $weight: 500);
  margin-bottom: $spacing-sm;
  width: 100%;
}

.resource-value {
  @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color), $weight: 500);
}

// Meter bar container
.meter-bar-container {
  height: 8px;
  background-color: $bg-light;
  border-radius: $border-radius-sm;
  overflow: hidden;
  margin-bottom: $spacing-xs;
  width: 100%;

  .meter-bar {
    height: 100%;
    border-radius: $border-radius-sm;
    transition: width 0.3s ease;
  }
}

// Resource info
.resource-info {
  @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color));
  width: 100%;
}

// Team members section
.team-members-content {
  @include flex-between;
  width: 100%;
}

.team-members-info {
  flex: 1;

  .team-members-title {
    @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color), $weight: 500);
    margin-bottom: $spacing-xs;
  }

  .team-members-count {
    @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color));
  }
}

// Circular meter styling
.circular-meter-container {
  @include flex-center;
  flex-shrink: 0;
}

.circular-meter {
  position: relative;

  .meter-text {
    @include absolute-fill;
    @include flex-center;
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
  }
}

// Upgrade button styling
.upgrade-btn {
  width: 100%;
  background-color: $bg-light;
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
  margin-top: $spacing-sm;
  border-radius: $border-radius-md;
  padding: $spacing-xs 0;
}

// Dropdown separator
.dropdown-separator {
  margin: 0;
  width: 100%;
}

// General list background
.q-list {
  background: $bg-white;
}

// Transition styles
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<script>
// Vuex
import { mapGetters } from 'vuex';

// Base Components
import BaseTooltip from '@base/BaseTooltip.vue';

/**
 * Component used to handle the user dropdown content
 *
 * @displayName UserDropdown
 */
export default {
  name: 'UserDropdown',
  components: {
    BaseTooltip
  },
  data() {
    return {
      tooltip: this.$t('COMING_SOON'),
      isNavigating: false,
      // Cache for resource metrics to prevent reset during navigation
      cachedMetrics: {
        formattedMaxStorage: '',
        formattedUsedStorage: '',
        storagePercentage: 0,
        storageBarColor: '',
        maxMembers: 0,
        totalMembers: 0,
        memberPercentage: 0,
        memberBarColor: ''
      },
      // Flag to track initial cache state
      metricsInitialized: false
    };
  },
  computed: {
    ...mapGetters({
      isAdmin: 'user/isAdmin',
      isEditor: 'user/isEditor',
      isPhotographer: 'user/isPhotographer',
      isSubClient: 'user/isSubClient',
      isClient: 'user/isClient',
      user: 'user/getUser',

      // Resources metrics getters
      maxStorageBytes: 'resources/maxStorageBytes',
      usedStorageBytes: 'resources/usedStorageBytes',
      formattedMaxStorage: 'resources/formattedMaxStorage',
      formattedUsedStorage: 'resources/formattedUsedStorage',
      storagePercentage: 'resources/storagePercentage',
      storageBarColor: 'resources/storageBarColor',
      maxMembers: 'resources/maxMembers',
      totalMembers: 'resources/totalMembers',
      memberCount: 'resources/memberCount',
      memberPercentage: 'resources/memberPercentage',
      memberBarColor: 'resources/memberBarColor'
    }),

    /**
     * Control which roles can view resource metrics
     * @returns {boolean} - Whether the current user can view the resource metrics
     */
    canViewResourceMetrics() {
      // Only clients can view metrics
      return this.isClient;
    },

    userInitial() {
      return (this.user && this.user.name && this.user.name.charAt(0).toUpperCase()) || 'U';
    },
    userRole() {
      if (this.isAdmin) return 'Admin';
      if (this.isPhotographer) return 'Photographer';
      if (this.isSubClient) return 'Sub-Client';
      return 'Client';
    },
    planName() {
      return this.user?.plan?.name || 'Starter Plan';
    },
    dropDownElements() {
      return [
        {
          name: this.$t('ACCOUNT_INFORMATION'),
          action: 'ACCOUNT_INFO',
          isDisabled: this.isAdmin || this.isEditor || this.isSubClient,
          icon: 'person'
        },
        {
          name: 'Notifications',
          action: 'SHOW_NOTIFICATIONS_SETTINGS',
          isDisabled: !this.isAdmin,
          icon: 'settings'
        },
        // {
        //   name: 'Integrations',
        //   action: 'SHOW_INTEGRATIONS',
        //   isDisabled: this.isEditor || this.isPhotographer,
        //   icon: 'hub'
        // },
        {
          name: 'AI assistant',
          action: 'GENERATE_QUOTATIONS',
          isDisabled: !this.isAdmin,
          icon: 'assistant'
        },
        {
          name: this.$t('PAYMENT_DETAILS'),
          action: 'PAYMENT_DETAILS',
          isDisabled: !this.isPhotographer,
          icon: 'credit_card'
        },
        // {
        //   name: 'Manage team',
        //   action: 'MANAGE_TEAM',
        //   isDisabled: this.isEditor || this.isSubClient || this.isPhotographer || this.isAdmin,
        //   icon: 'group_add'
        // },
        {
          icon: 'logout',
          name: 'Logout',
          action: 'LOGOUT'
        }
      ];
    }
  },
  watch: {
    // Watch resource metrics and cache them when they change
    formattedMaxStorage(val) {
      if (val && this.canViewResourceMetrics) {
        this.cachedMetrics.formattedMaxStorage = val;
      }
    },
    formattedUsedStorage(val) {
      if (val && this.canViewResourceMetrics) {
        this.cachedMetrics.formattedUsedStorage = val;
      }
    },
    storagePercentage(val) {
      if (val !== undefined && this.canViewResourceMetrics) {
        this.cachedMetrics.storagePercentage = val;
      }
    },
    storageBarColor(val) {
      if (val && this.canViewResourceMetrics) {
        this.cachedMetrics.storageBarColor = val;
      }
    },
    maxMembers(val) {
      if (val !== undefined && this.canViewResourceMetrics) {
        this.cachedMetrics.maxMembers = val;
      }
    },
    totalMembers(val) {
      if (val !== undefined && this.canViewResourceMetrics) {
        this.cachedMetrics.totalMembers = val;
      }
    },
    memberPercentage(val) {
      if (val !== undefined && this.canViewResourceMetrics) {
        this.cachedMetrics.memberPercentage = val;
      }
    },
    memberBarColor(val) {
      if (val && this.canViewResourceMetrics) {
        this.cachedMetrics.memberBarColor = val;
      }
    }
  },
  mounted() {
    // Initialize the cache with current values
    this.initializeMetricsCache();
  },
  methods: {
    /**
     * Initialize the cache with current metrics values
     */
    initializeMetricsCache() {
      if (this.canViewResourceMetrics && !this.metricsInitialized) {
        this.cachedMetrics = {
          formattedMaxStorage: this.formattedMaxStorage || '',
          formattedUsedStorage: this.formattedUsedStorage || '',
          storagePercentage: this.storagePercentage || 0,
          storageBarColor: this.storageBarColor || '#2196f3',
          maxMembers: this.maxMembers || 0,
          totalMembers: this.totalMembers || 0,
          memberPercentage: this.memberPercentage || 0,
          memberBarColor: this.memberBarColor || '#2196f3'
        };
        this.metricsInitialized = true;
      }
    },

    /**
     * Method used to perform a specific action based on the link clicked from the dropdown
     */
    handleAction(action) {
      // Prevent multiple clicks while navigating
      if (this.isNavigating) return;

      this.isNavigating = true;

      // Close the dropdown
      this.$root.$emit('close-user-dropdown');

      const actions = {
        ACCOUNT_INFO: () => this.$router.push({ name: 'account' }),
        LOGOUT: () => this.handleLogout(),
        PAYMENT_DETAILS: () => this.$router.push({ name: 'paymentDetails' }),
        SHOW_NOTIFICATIONS_SETTINGS: () => this.$router.push({ name: 'notificationsSettings' }),
        GENERATE_QUOTATIONS: () => this.$router.push({ name: 'quotes' }),
        SHOW_INTEGRATIONS: () => this.$router.push({ name: 'integrations' }),
        MANAGE_TEAM: () => this.$router.push({ name: 'subAccounts' })
      };

      // Add a small delay before navigation to allow dropdown to close
      setTimeout(() => {
        actions[action]();

        // Reset navigation state after transition
        setTimeout(() => {
          this.isNavigating = false;
        }, 300);
      }, 50);
    },

    /**
     * Navigate to the subscription page to upgrade the plan
     */
    handleUpgrade() {
      // Prevent multiple clicks while navigating
      if (this.isNavigating) return;

      this.isNavigating = true;

      // Close the dropdown first to prevent UI flicker
      this.$root.$emit('close-user-dropdown');

      // Short delay to let the dropdown animation complete
      setTimeout(() => {
        this.$router.push({ name: 'billing-payments' });

        // Reset navigation state after a delay to ensure transition is complete
        setTimeout(() => {
          this.isNavigating = false;
        }, 300);
      }, 50);
    }
  },
  // Add activated hook for when component is reactivated from keep-alive
  activated() {
    if (this.canViewResourceMetrics && this.metricsInitialized) {
      this.$forceUpdate();
    }
  }
};
</script>
